// EnemyView: every appliance robot, one instanced draw per TYPE.
//
// Each robot type is a small rigid hierarchy (body + 2-8 moving parts + eyes) merged into one
// geometry whose vertices carry their part slot; the robot root is the instance matrix and the
// part matrices live in a per-type float texture (see kit.ts TypeBatch). Ink outlines and decals
// are baked into the same geometry, so a type costs ONE colour draw + ONE shadow draw however
// many robots it has on screen. Shields/hyper shells, prime diamonds and sniper lasers are one
// instanced draw each, shared across types.
//
// Draw calls with all 8 types on screen: 8 colour + 8 shadow + shells + diamonds + lasers = 19.
// Robots that vanish from the list right after being hit play a death (topple, parts pop off,
// scorch, sink) from their last pose. Allocation-free per frame.

import * as THREE from 'three';
import type { EnemyRS, EnemyViewApi, ViewCtx } from '../contracts';
import { DeathKind } from '../contracts';
import { ENEMIES } from '../../../sim/defs';
import type * as CANNONT from 'cannon-es';

// ---- the ragdoll (weight lane): a robot blown off its feet by a big enough hit dies as a handful of rigid
// bodies (cannon-es, the engine the stone falls already use, loaded on first need): its shell and every part,
// thrown by the blow, tumbling and bouncing on the ground where it fell, parts scattering. Client-only and
// cosmetic: the sim is long done with the robot. Capped so a wipe never costs the frame budget.
let CANNON: typeof CANNONT | null = null;
let cannonLoading: Promise<void> | null = null;
function loadCannonLib() { if (!cannonLoading) cannonLoading = import('cannon-es').then((m) => { CANNON = m; }); return cannonLoading; }
interface Ragdoll { world: CANNONT.World; bodies: (CANNONT.Body | null)[]; root: THREE.Matrix4; rootInv: THREE.Matrix4; acc: number }
const RAG_MAX = 6;
/** a Blown death at least this hard (hit power against the hull) ragdolls; below it the analytic arc plays */
const RAG_POW = 0.9;
const RAG_STEP = 1 / 60;
let ragCount = 0;
import { PAL } from '../palette';
import { Fx, Rig, TypeBatch } from './kit';
import { robotMaterial, PART_DECL } from './material';
import { robotShieldMaterial } from '../fx/shieldskin';
import { Blobs, Diamonds, Lasers, Shells } from './fx';
import {
  BOMBER, BRUTE, DISRUPTOR, ENGINEER, SHOOTER, SNIPER, SWARMER,
  buildBomber, buildBrute, buildDisruptor, buildEngineer, buildEye, buildShooter, buildSniper, buildSwarmer, setLod,
} from './models';

const SWARMER_T = 0, SHOOTER_T = 1, BRUTE_T = 2, SNIPER_T = 3, BOMBER_T = 4, METAL_T = 5, DISRUPTOR_T = 6, ENGINEER_T = 7;
const N_TYPES = 8;
/** the Stalker (sim/defs.ts EType.Stalker) is drawn on the Swarmer kit, cloaked; the Swarm Captain is a Swarmer at CAPTAIN_S */
const STALKER_T = 18, CAPTAIN_S = 1.6, TURRET_T = 19, TURRET_S = 0.55;
/** the fliers (sim/defs.ts 20-22) ride the Bomber kit (it has the jets): the Flying Sniper leaner, the helicopter bigger */
const FSHOOTER_T = 20, FSNIPER_T = 21, HELI_T = 22, HELI_S = 1.35;
const isFlierT = (t: number) => t >= FSHOOTER_T && t <= HELI_T;
const kitType = (t: number) => (t === STALKER_T ? SWARMER_T : t === TURRET_T ? ENGINEER_T : isFlierT(t) ? BOMBER_T : t);
const METAL_S = 1.6;
const CAP = 48, CAP_METAL = 4;
const F_PRIMED = 1, F_HYPER = 2, F_STUN = 4, F_BURN = 8, F_VULN = 16, F_STRIP = 32, F_AIR = 64;
const DIE_LEN = 3.4;
/** impact freeze on a kill (hit-stop): the corpse holds the hit pose this long, flashing, before it falls */
const DIE_HOLD = 0.07;
/** the part that pops on a headshot kill, per type (kit slot name) */
const HEAD_PART = ['toast', 'fan', 'lever', 'rifle', 'lid', 'lever', 'face', 'light'];
/** lowest point of each type's body part above its feet: a corpse collapses this far (the legs
 *  give way) so it lands on its own underside, then tips over its back-bottom edge */
const BOT = [0.14, 0.26, 0.53, 0.22, 0.34, 0.53, 0.55, 0.96];

/** Layout for types the sim doesn't define yet (the sheet's Disruptor and Engineer). */
interface Def { radius: number; bodyY: number; headY: number; headR: number; speed: number }
const DEFS: Def[] = [
  ...ENEMIES,
  { radius: 0.7, bodyY: 1.6, headY: 2.0, headR: 0.36, speed: 3.8 },
  { radius: 0.9, bodyY: 1.8, headY: 1.95, headR: 0.4, speed: 3.0 },
];
const LAYOUT = [SWARMER, SHOOTER, BRUTE, SNIPER, BOMBER, BRUTE, DISRUPTOR, ENGINEER];
/** how hard a hit knocks each type back (radians) */
const FLINCH = [0.24, 0.14, 0.08, 0.16, 0.12, 0.04, 0.16, 0.12];
/** contact-blob footprint per type (width, depth in metres, before the Metal Brute's scale) */
const BLOB = [[1.7, 1.4], [1.75, 1.6], [2.6, 2.0], [2.3, 1.7], [2.1, 1.7], [2.6, 2.0], [1.75, 1.75], [2.2, 1.9]];

const lin = (hex: number) => new THREE.Color(hex);
const C_MAG = lin(PAL.primeMagenta), C_AMB = lin(PAL.primeAmber);
const C_BURN = lin(PAL.thrusterRing), C_VULN = lin(PAL.brassTrim), C_RIM = lin(PAL.sunCore);
const C_SHIELD = lin(PAL.shieldBlue), C_HYPER = lin(PAL.hyperMagenta), C_ACID = lin(PAL.grassGreen);
const C_ICE = lin(0xbfeeff), C_WET = lin(0x3a5a78), C_STALK = lin(0x9a4cff), C_REDSHELL = lin(0xff4a40), C_GOLD = lin(0xffc23a), C_HELI = lin(0x5ff2e0);
/** the element a robot carries (sim/protocol.ts EFlag2 bits 4-6: Fire 1, Acid 2, Shock 3, Ice 4, Magnetic 5) in its colour */
const C_ELEM = [null, C_BURN, C_ACID, C_SHIELD, C_ICE, C_MAG];

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
const ease = (t: number) => t * t * (3 - 2 * t);
const outBack = (t: number) => { const c = 1.9; const u = t - 1; return 1 + (c + 1) * u * u * u + c * u * u; };
function outBounce(t: number) {
  if (t < 1 / 2.75) return 7.5625 * t * t;
  if (t < 2 / 2.75) { t -= 1.5 / 2.75; return 7.5625 * t * t + 0.75; }
  if (t < 2.5 / 2.75) { t -= 2.25 / 2.75; return 7.5625 * t * t + 0.9375; }
  t -= 2.625 / 2.75; return 7.5625 * t * t + 0.984375;
}
const hash = (n: number) => { const s = Math.sin(n * 127.1) * 43758.5453; return s - Math.floor(s); };

class Anim {
  id = -1;
  seen = 0;
  phase = 0;
  fan = 0;
  fanRate = 5;
  drum = 0;
  wheel = 0;
  lever = Math.PI - 0.35;
  rifle = 0;
  blinkT = 2;
  blink = 1;
  spd = 0;
  acc = 0;
  yaw = 0;
  /** weight lane: this death ragdolls; the rigid bodies once built; each part's local pose recorded on the first dying frame */
  ragWant = false;
  rag: Ragdoll | null = null;
  ragLocals: number[][] = [];
  turn = 0;
  /** -1 alive, else seconds since death */
  dieT = -1;
  /** shooting feel: last hit's world push direction + power, and how it dies */
  hdx = 0; hdz = 0; hpow = 0;
  dKind = 0; ddx = 0; ddz = 1; dpow = 0;
  /** blown-off-its-feet arc: launch velocity (world) and spin */
  bvx = 0; bvy = 0; bvz = 0; bspin = 0;
  /** Swarmer perch leap (sim/perch.ts): seconds in the air (-1 grounded), since landing, flip way */
  leapT = -1; landT = 9; leapDir = 1;
  /** the shield skin (fx/shieldskin.ts): last seen shield, when it broke, the last strike on it (world) */
  shPrev = 0; shBrkAt = -99; shHitAt = -99; shX = 0; shY = 0; shZ = 0;
  /** last seen state, the corpse's pose */
  snap: EnemyRS = {
    id: 0, type: 0, x: 0, y: 0, z: 0, yaw: 0, st: 0, prog: 0, hp: 1, shield: 0, flags: 0, hitAge: 99, speed: 0,
    lookX: 0, lookY: 0, lookZ: 0, aimX: 0, aimY: 0, aimZ: 0, hasAim: false,
  };
}

interface TypeKit { b: TypeBatch; s: { [part: string]: number } }

/** which rig slots a robot's shot-off parts (sim/defs.ts Part bits) own, by kit: the top part is HEAD_PART; the arms are the
 *  'arm' slots (one arm counts as the right); the legs are the first leg / foot / track / wheel / stilt slot */
function partSlots(s: Record<string, number>, t: number, parts: number): number {
  if (!parts) return 0;
  const starts = Object.values(s).sort((a, b) => a - b);
  const count = (name: string) => { const st = s[name]; if (st === undefined) return 0; const i = starts.indexOf(st); return (i + 1 < starts.length ? starts[i + 1] : st + 1) - st; };
  let mask = 0;
  if (parts & 1) { const st = s[HEAD_PART[t]]; if (st !== undefined) mask |= 1 << st; }
  if (parts & 2) { const st = s['arm']; if (st !== undefined) mask |= 1 << (st + (count('arm') >= 2 ? 1 : 0)); }
  if (parts & 4) { const st = s['arm']; if (st !== undefined && count('arm') >= 2) mask |= 1 << st; }
  if (parts & 8) { for (const n of ['leg', 'foot', 'track', 'wheel', 'stilt']) { const st = s[n]; if (st !== undefined) { mask |= 1 << st; break; } } }
  return mask;
}

export class EnemyView implements EnemyViewApi {
  private ctx: ViewCtx;
  private group = new THREE.Group();
  private kits: TypeKit[] = [];
  private all: TypeBatch[] = [];
  private shells: Shells;
  private diamonds: Diamonds;
  private lasers: Lasers;
  private blobs: Blobs;
  private hiQ: boolean;

  private pool: Anim[] = [];
  private active: Anim[] = [];
  private byId = new Map<number, Anim>();
  private frame = 0;

  // per-robot scratch
  private cur!: TypeKit;
  private dying = 0;
  private dT = 0;
  private dieSeed = 0;
  /** death scratch: hit-stop hold (s), shatter progress 0..1, the part that pops on a headshot */
  private hold = 0;
  private shatter = 0;
  private headSlot = -1;
  /** visible damage (playtest 3): the slots of the parts shot off this robot (bit per slot), hidden while it lives */
  private hiddenSlots = 0;
  private dieAnim: Anim | null = null;
  private fx = new Fx();
  private R = new THREE.Matrix4();
  private Ri = new THREE.Matrix4();
  private L = new THREE.Matrix4();
  private W = new THREE.Matrix4();
  private T = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private eu = new THREE.Euler();
  private v = new THREE.Vector3();
  private v2 = new THREE.Vector3();
  private v3 = new THREE.Vector3();
  private sc = new THREE.Vector3();
  private col = new THREE.Color();
  private up = new THREE.Vector3(0, 1, 0);
  private zero = new THREE.Vector3();

  constructor(ctx: ViewCtx) {
    this.ctx = ctx;
    this.group.name = 'robots';
    const shadow = ctx.quality >= 1;
    this.hiQ = ctx.quality >= 1;
    setLod(ctx.quality);
    const eye = buildEye();
    const mk = (t: number, rig: Rig, s: { [k: string]: number }, cap: number, name: string) => {
      if (LAYOUT[t].eyes.length) s.eye = rig.add(eye, LAYOUT[t].eyes.length, 'none');
      const geo = rig.build(shadow);
      const h = LAYOUT[t].top * (t === METAL_T ? METAL_S : 1);
      const b = new TypeBatch(geo, rig.slots, cap, shadow, name, (tex) => robotMaterial(tex, h), shadow ? rig.mainCount : 0);
      this.group.add(b.mesh, b.addShield(robotShieldMaterial(PART_DECL, b.tex, h)));
      this.all.push(b);
      this.kits[t] = { b, s };
    };
    {
      const p = buildSwarmer().parts, r = new Rig();
      mk(SWARMER_T, r, { body: r.add(p.body, 1, new THREE.BoxGeometry(1.04, 0.86, 0.6).translate(0, 0.63, 0)), toast: r.add(p.toast, 2), foot: r.add(p.foot, 4) }, CAP, 'robot-swarmer');
    }
    {
      const p = buildShooter().parts, r = new Rig();
      mk(SHOOTER_T, r, { body: r.add(p.body, 1, 'round'), fan: r.add(p.fan, this.hiQ ? 3 : 1, 'none'), track: r.add(p.track, 2), arm: r.add(p.arm, 2) }, CAP, 'robot-shooter');
    }
    // dusty mint (the ref's boiler green, not lime): body, dome, slightly deeper pauldrons
    const bruteInks = { body: 0x9cbca6, dome: 0x9cbca6, shoulder: 0x8aab95, seam: 0x2a2420, rivet: 0x2a2420, rust: 0.6, metal: false };
    {
      const p = buildBrute(1, bruteInks).parts, r = new Rig();
      mk(BRUTE_T, r, { body: r.add(p.body, 1, 'round'), lever: r.add(p.lever), leg: r.add(p.leg, 2), arm: r.add(p.arm, 2) }, CAP, 'robot-brute');
    }
    {
      const p = buildSniper().parts, r = new Rig();
      mk(SNIPER_T, r, { body: r.add(p.body, 1, 'round'), wheel: r.add(p.wheel, 2, 'round'), rifle: r.add(p.rifle) }, CAP, 'robot-sniper');
    }
    {
      const p = buildBomber().parts, r = new Rig();
      mk(BOMBER_T, r, { body: r.add(p.body), lid: r.add(p.lid), door: r.add(p.door), arm: r.add(p.arm), bomb: r.add(p.bomb), leg: r.add(p.leg, 2) }, CAP, 'robot-bomber');
    }
    const metalInks = { body: PAL.chrome, dome: PAL.steel, shoulder: PAL.steel, seam: PAL.inkDark, rivet: PAL.brass, rust: 0.45, metal: true };
    {
      const p = buildBrute(METAL_S, metalInks).parts, r = new Rig();
      mk(METAL_T, r, { body: r.add(p.body, 1, 'round'), lever: r.add(p.lever), leg: r.add(p.leg, 2), arm: r.add(p.arm, 2) }, CAP_METAL, 'robot-metal-brute');
    }
    {
      const p = buildDisruptor().parts, r = new Rig();
      mk(DISRUPTOR_T, r, { body: r.add(p.body), face: r.add(p.smile, 1, 'none'), angry: r.add(p.angry, 1, 'none'), stat: r.add(p.stat, 1, 'none'), caster: r.add(p.caster), leg: r.add(p.leg, 2), arm: r.add(p.arm) }, CAP, 'robot-disruptor');
    }
    {
      const p = buildEngineer().parts, r = new Rig();
      mk(ENGINEER_T, r, { body: r.add(p.body), light: r.add(p.light, 1, 'none'), stilt: r.add(p.stilt, 3), arm: r.add(p.arm) }, CAP, 'robot-engineer');
    }

    const robots = CAP * 7 + CAP_METAL;
    this.shells = new Shells(robots);
    this.diamonds = new Diamonds(robots);
    this.lasers = new Lasers(CAP, PAL.laserRed);
    this.blobs = new Blobs(robots);
    this.group.add(this.shells.mesh, this.diamonds.mesh, this.lasers.mesh, this.blobs.mesh);
    for (let i = 0; i < 256; i++) this.pool.push(new Anim());
    ctx.scene.add(this.group);
  }

  // ------------------------------------------------------------------ frame
  sync(list: readonly EnemyRS[], count: number, dt: number, time: number): void {
    this.frame++;
    this.now = time;
    dt = Math.min(dt, 0.1);
    for (const b of this.all) b.begin();
    this.shells.begin(); this.diamonds.begin(); this.lasers.begin(); this.blobs.begin();
    for (let i = 0; i < count; i++) {
      const e = list[i];
      const kt = kitType(e.type);
      if (kt < 0 || kt >= N_TYPES) continue;
      const a = this.anim(e.id);
      const s = a.snap;
      s.id = e.id; s.type = kt; s.x = e.x; s.y = e.y; s.z = e.z; s.yaw = e.yaw; s.st = e.st; s.prog = e.prog;
      s.hp = e.hp; s.flags = e.flags; s.hitAge = e.hitAge; s.lookX = e.lookX; s.lookY = e.lookY; s.lookZ = e.lookZ;
      if (e.hitPow !== undefined) { a.hdx = e.hitDX ?? 0; a.hdz = e.hitDZ ?? 0; a.hpow = e.hitPow; }
      this.dying = 0;
      this.robot(e, a, dt, time);
    }
    // robots that left the list: the ones just hit die on screen, the rest are released
    for (let i = this.active.length - 1; i >= 0; i--) {
      const a = this.active[i];
      if (a.seen === this.frame) continue;
      if (a.dieT < 0) {
        if (a.seen === this.frame - 1 && (a.snap.hitAge < 1.2 || a.snap.hp < 0.15 || a.dpow > 0)) {
          a.dieT = 0;
          if (a.dpow === 0) { a.ddx = a.hdx; a.ddz = a.hdz; a.dpow = Math.max(0.3, a.hpow); }
          if (a.dKind === DeathKind.Blown) {
            const p = Math.min(2, a.dpow), w = ENEMIES[a.snap.type]?.weight ?? 1;
            const k = 1 / Math.sqrt(Math.max(1, w));
            a.bvx = a.ddx * (3 + 4 * p) * k; a.bvz = a.ddz * (3 + 4 * p) * k; a.bvy = (5 + 4 * p) * k;
            a.bspin = (hash(a.id * 1.9) < 0.5 ? -1 : 1) * (4 + 5 * p) * k;
            // a big enough blow ragdolls it (the physics engine loads on the first one; that first one plays the arc)
            if (a.dpow >= RAG_POW && ragCount < RAG_MAX && this.ctx.quality >= 1) { if (CANNON) a.ragWant = true; else loadCannonLib(); }
          }
        }
        else { this.release(i); continue; }
      } else a.dieT += dt;
      if (a.dieT > DIE_LEN) { this.release(i); continue; }
      const s = a.snap;
      s.st = 0; s.speed = 0; s.shield = 0; s.flags &= F_BURN; s.hitAge = a.dieT; s.hasAim = false;
      this.dying = 1;
      this.dT = a.dieT;
      this.dieSeed = a.id;
      this.dieAnim = a;
      if (a.rag) { const r = a.rag; r.acc = Math.min(r.acc + dt, 0.1); while (r.acc >= RAG_STEP) { r.world.step(RAG_STEP); r.acc -= RAG_STEP; } }
      this.robot(s, a, dt, time);
      if (a.ragWant && !a.rag && a.ragLocals.length) this.buildRagdoll(a, s);
      a.seen = this.frame - 1; // keep "last frame" semantics so it stays a corpse
    }
    this.dying = 0;
    for (const b of this.all) b.end();
    this.shells.end(time); this.diamonds.end(); this.lasers.end(time); this.blobs.end();
  }

  /** a shot stopped by this robot's shield at a world point: its skin lights from there (fx/shieldskin.ts) */
  shieldHit(id: number, x: number, y: number, z: number) {
    const a = this.byId.get(id);
    if (!a) return;
    // a predicted hit and the server's confirm of the same shot land a few frames apart: keep the first
    if (this.now - a.shHitAt < 0.06) return;
    a.shHitAt = this.now; a.shX = x; a.shY = y; a.shZ = z;
  }
  /** a robot's shield just went (world feet point and body height): game.ts plays the shatter */
  onShieldBreak: ((x: number, y: number, z: number, h: number) => void) | null = null;
  private now = 0;

  /** the killing blow (client/feel): call just before the robot leaves the list */
  kill(id: number, kind: DeathKind, dx: number, dz: number, pow: number) {
    const a = this.byId.get(id);
    if (!a) return;
    const l = Math.hypot(dx, dz);
    a.dKind = kind; a.ddx = l > 1e-3 ? dx / l : 0; a.ddz = l > 1e-3 ? dz / l : 0; a.dpow = Math.max(0.3, pow);
  }

  dispose() {
    this.ctx.scene.remove(this.group);
    for (const b of this.all) b.dispose();
    for (const m of [this.shells.mesh, this.diamonds.mesh, this.lasers.mesh, this.blobs.mesh]) {
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    }
  }

  private anim(id: number): Anim {
    let a = this.byId.get(id);
    if (!a) {
      a = this.pool.pop() ?? new Anim();
      a.id = id; a.phase = (id * 1.7) % 6.28; a.fan = 0; a.fanRate = 5; a.drum = 0; a.wheel = 0;
      a.lever = Math.PI - 0.35; a.rifle = 0; a.blinkT = 1 + ((id * 0.37) % 3); a.blink = 1;
      a.spd = 0; a.acc = 0; a.turn = 0; a.yaw = NaN; a.dieT = -1;
      a.hdx = 0; a.hdz = 0; a.hpow = 0; a.dKind = DeathKind.Topple; a.ddx = 0; a.ddz = 1; a.dpow = 0;
      a.ragWant = false; a.rag = null; a.ragLocals.length = 0;
      a.shPrev = 0; a.shBrkAt = -99; a.shHitAt = -99;
      this.byId.set(id, a);
      this.active.push(a);
    }
    a.seen = this.frame;
    a.dieT = -1;
    return a;
  }

  /** the rigid bodies of a ragdolled corpse, from the part poses the first dying frame recorded */
  private buildRagdoll(a: Anim, e: EnemyRS) {
    const C = CANNON;
    if (!C) { a.ragWant = false; return; }
    const t = kitType(e.type), def = DEFS[t], lay = LAYOUT[t], s = t === METAL_T ? METAL_S : (e.ext ?? 0) & 16 ? CAPTAIN_S : 1, weight = ENEMIES[e.type]?.weight ?? 1;
    const world = new C.World({ gravity: new C.Vec3(0, -14, 0), allowSleep: true });
    world.defaultContactMaterial.friction = 0.5;
    world.defaultContactMaterial.restitution = 0.18;
    const ground = new C.Body({ mass: 0 });
    ground.addShape(new C.Plane());
    ground.quaternion.setFromEuler(-Math.PI / 2, 0, 0);
    ground.position.set(0, e.y, 0);
    world.addBody(ground);
    const root = new THREE.Matrix4().makeRotationY(e.yaw).setPosition(e.x, e.y, e.z);
    const rq = new THREE.Quaternion().setFromRotationMatrix(root);
    const p = Math.min(2, a.dpow), k = 1 / Math.sqrt(Math.max(1, weight));
    const bodies: (CANNONT.Body | null)[] = [];
    for (let slot = 0; slot < a.ragLocals.length; slot++) {
      const L = a.ragLocals[slot];
      if (!L) { bodies.push(null); continue; }
      const [x, y, z, rx, ry, rz, sc] = L;
      const pos = new THREE.Vector3(x, y, z).applyMatrix4(root);
      const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz)).premultiply(rq);
      const body = new C.Body({ mass: slot === 0 ? 4 + weight * 2 : 0.8, linearDamping: 0.08, angularDamping: 0.2 });
      if (slot === 0) {
        // the shell: a box the size of the robot, centred on its middle (the part's origin may sit at its feet)
        const hr = Math.max(0.2, def.radius * 0.7 * sc), hy = Math.max(0.2, lay.top * s * 0.45);
        body.addShape(new C.Box(new C.Vec3(hr, hy, hr)), new C.Vec3(0, lay.top * s * 0.5 - y, 0));
      } else {
        const h = 0.16 * s * Math.max(0.5, sc);
        body.addShape(new C.Box(new C.Vec3(h, h, h)));
      }
      body.position.set(pos.x, pos.y, pos.z);
      body.quaternion.set(q.x, q.y, q.z, q.w);
      const h1 = hash(a.id * 3.1 + slot), h2 = hash(a.id * 7.7 + slot * 1.3);
      const scat = slot === 0 ? 0 : 1.5 + 2.5 * p;
      body.velocity.set(a.ddx * (3 + 4 * p) * k + (h1 - 0.5) * scat, (5 + 4 * p) * k + h2 * scat * 0.6, a.ddz * (3 + 4 * p) * k + (h2 - 0.5) * scat);
      body.angularVelocity.set((h2 - 0.5) * 12 * k, (h1 - 0.5) * 6, (h1 - 0.5) * 12 * k + (slot === 0 ? a.bspin : 0));
      world.addBody(body);
      bodies.push(body);
    }
    a.rag = { world, bodies, root, rootInv: root.clone().invert(), acc: 0 };
    a.ragWant = false;
    ragCount++;
  }

  private release(i: number) {
    const a = this.active[i];
    if (a.rag) { ragCount--; a.rag = null; }
    a.ragWant = false; a.ragLocals.length = 0;
    this.byId.delete(a.id);
    this.active[i] = this.active[this.active.length - 1];
    this.active.pop();
    this.pool.push(a);
  }

  // ------------------------------------------------------------------ per robot
  private robot(e: EnemyRS, a: Anim, dt: number, time: number) {
    const t = kitType(e.type);
    const def = DEFS[t];
    const lay = LAYOUT[t];
    const metal = t === METAL_T;
    const ext = e.ext ?? 0;
    const s = metal ? METAL_S : ext & 16 ? CAPTAIN_S : e.type === TURRET_T ? TURRET_S : e.type === HELI_T ? HELI_S : e.type === FSNIPER_T ? 0.9 : 1; // the Swarm Captain is a big Swarmer; the turret a small Engineer; the helicopter a big Bomber
    const dying = this.dying === 1;
    const dT = a.dieT;
    this.hold = dying ? DIE_HOLD * (a.dKind === DeathKind.Headshot ? 1.4 : 1) * Math.min(1.4, 0.7 + a.dpow * 0.5) : 0;
    this.shatter = dying && a.dKind === DeathKind.Shatter ? clamp01((dT - this.hold) / 0.07) : 0;
    this.headSlot = dying && a.dKind === DeathKind.Headshot ? (this.kits[t].s[HEAD_PART[t]] ?? -1) : -1;
    this.hiddenSlots = dying ? 0 : partSlots(this.kits[t].s, t, e.parts ?? 0);
    const st = e.st, p = clamp01(e.prog);
    const spdN = Math.min(e.speed / def.speed, 1.6);
    const stunned = !dying && (st === 4 || (e.flags & F_STUN) !== 0);
    const top = lay.top * s;

    // ---- motion state: smoothed speed, acceleration (lean), turn rate (bank)
    if (!dying) {
      const ps = a.spd;
      a.spd += (e.speed - a.spd) * Math.min(1, dt * 8);
      const acc = dt > 0 ? (a.spd - ps) / dt : 0;
      a.acc += (acc - a.acc) * Math.min(1, dt * 6);
      if (a.yaw !== a.yaw) a.yaw = e.yaw;
      let dy = e.yaw - a.yaw;
      dy -= Math.round(dy / (Math.PI * 2)) * Math.PI * 2;
      a.yaw = e.yaw;
      const tr = dt > 0 ? dy / dt : 0;
      a.turn += (Math.max(-6, Math.min(6, tr)) - a.turn) * Math.min(1, dt * 5);
    }

    // ---- status → instance fx
    const f = this.fx;
    f.seed = ((e.id * 0.618034) % 1) * 7;
    let er = 0, eg = 0, eb = 0;
    if (e.flags & F_PRIMED) {
      const pulse = 0.5 + 0.5 * Math.sin(time * 7 + e.id);
      this.col.copy(C_MAG).lerp(C_AMB, pulse);
      const k = 0.12 + 0.2 * pulse;
      er += this.col.r * k; eg += this.col.g * k; eb += this.col.b * k;
    }
    if (e.flags & F_BURN) {
      const fl = 0.5 + 0.5 * Math.sin(time * 23 + e.id * 3) * Math.sin(time * 13.7 + e.id);
      const k = 0.18 + 0.32 * fl;
      er += C_BURN.r * k; eg += C_BURN.g * k; eb += C_BURN.b * k;
    }
    if (stunned) {
      // shock: hard white-blue crackle, on/off like an arc
      const on = Math.sin(time * 61 + e.id * 5) * Math.sin(time * 37) > 0.25 ? 1 : 0;
      const k = 0.08 + 0.3 * on;
      er += C_SHIELD.r * k; eg += C_SHIELD.g * k; eb += C_SHIELD.b * k;
    }
    if (e.flags & F_STRIP) {
      const k = 0.1 + 0.08 * Math.sin(time * 5 + e.id);
      er += C_ACID.r * k; eg += C_ACID.g * k; eb += C_ACID.b * k;
    }
    // the elements (sim/elements.ts): frozen glows ice-white and holds still; wet is a dark sheen; a robot that carries an
    // element wears a steady glow in its colour (the rock-paper-scissors read)
    const f2 = e.flags2 ?? 0, frozen = (f2 & 4) !== 0 && !dying, wet = (f2 & 8) !== 0, carried = C_ELEM[(f2 >> 4) & 7];
    if (frozen) { const k = 0.3 + 0.1 * Math.sin(time * 3 + e.id); er += C_ICE.r * k; eg += C_ICE.g * k; eb += C_ICE.b * k; }
    else if ((f2 & 1) !== 0 && !dying) { const k = 0.14 + 0.1 * Math.abs(Math.sin(time * 40 + e.id)); er += C_MAG.r * k; eg += C_MAG.g * k; eb += C_MAG.b * k; } // the Time grenade's violet smear
    else if (carried) { const k = 0.1 + 0.04 * Math.sin(time * 4 + e.id); er += carried.r * k; eg += carried.g * k; eb += carried.b * k; }
    f.fx[0] = er; f.fx[1] = eg; f.fx[2] = eb;
    // hit: a short bright rim flash (~60 ms) and only a light whiten, never a full white-out
    const hk = dying ? (dT < this.hold ? 1 : Math.max(0, 1 - (dT - this.hold) / 0.1)) : e.hitAge < 0.06 ? 1 - e.hitAge / 0.06 : 0;
    const dull = !dying && e.hitDull ? 0.25 : 1;
    f.fx[3] = hk * 0.28 * dull;
    // the Stalker's cloak (ROSTER.md): a negative whiten is the screen-door amount in the shader; a violet shimmer rides on it
    if (ext & 8) { f.fx[3] = -0.72 + hk * 0.4; er += C_STALK.r * 0.12; eg += C_STALK.g * 0.12; eb += C_STALK.b * 0.12; }
    else if (e.type === STALKER_T) { er += C_STALK.r * 0.2; eg += C_STALK.g * 0.2; eb += C_STALK.b * 0.2; }
    if (ext & 128) { er += C_GOLD.r * 0.22; eg += C_GOLD.g * 0.22; eb += C_GOLD.b * 0.22; } // a gold bounty robot (CHASE.md)
    if (isFlierT(e.type)) { const fc = e.type === HELI_T ? C_HELI : e.type === FSNIPER_T ? C_REDSHELL : C_SHIELD; const k = (e.flags & F_AIR) ? 0.16 + 0.06 * Math.sin(time * 9 + e.id) : 0.04; er += fc.r * k; eg += fc.g * k; eb += fc.b * k; } // the fliers' jets glow in their colour
    if (e.type === STALKER_T || ext & 128 || isFlierT(e.type)) { f.fx[0] = er; f.fx[1] = eg; f.fx[2] = eb; }
    if (e.flags & F_VULN) {
      const k = 1.3 + 0.4 * Math.sin(time * 9);
      f.rim[0] = C_VULN.r * k; f.rim[1] = C_VULN.g * k; f.rim[2] = C_VULN.b * k;
    } else if (e.flags & F_STRIP) {
      f.rim[0] = C_ACID.r * 0.9; f.rim[1] = C_ACID.g * 0.9; f.rim[2] = C_ACID.b * 0.9;
    } else if (frozen) {
      f.rim[0] = C_ICE.r * 1.4; f.rim[1] = C_ICE.g * 1.4; f.rim[2] = C_ICE.b * 1.4;
    } else if (ext & 128) {
      f.rim[0] = C_GOLD.r * 1.3; f.rim[1] = C_GOLD.g * 1.3; f.rim[2] = C_GOLD.b * 1.3;
    } else if (carried) {
      f.rim[0] = carried.r * 0.7; f.rim[1] = carried.g * 0.7; f.rim[2] = carried.b * 0.7;
    } else if (wet) {
      f.rim[0] = C_WET.r; f.rim[1] = C_WET.g; f.rim[2] = C_WET.b;
    } else {
      f.rim[0] = C_RIM.r * 0.5; f.rim[1] = C_RIM.g * 0.5; f.rim[2] = C_RIM.b * 0.5;
    }
    if (hk > 0) { const k = 0.6 + 1.8 * hk * dull; f.rim[0] = f.rim[0] * 0.3 + k; f.rim[1] = f.rim[1] * 0.3 + k * 0.95; f.rim[2] = f.rim[2] * 0.3 + k * 0.85; }
    // charred: scorch comes in over 0.4 s and holds until the corpse sinks (>= 2 s)
    // scorch: a robot wears its damage (playtest 3): the char deepens as the hull goes, more on fire, full on death
    f.rim[3] = dying ? Math.min(1, dT * 2.5) * 0.75 : Math.max((e.flags & F_BURN) ? 0.18 : 0, (1 - clamp01(e.hp)) * 0.5);

    // ---- gait clock + blink
    const stride = t === SWARMER_T ? 2.8 : t === BRUTE_T || metal ? 1.5 / s : t === BOMBER_T ? 2.2 : t === ENGINEER_T ? 1.6 : t === DISRUPTOR_T ? 2.4 : 2.0;
    if (!dying && !frozen && e.type !== TURRET_T) a.phase += dt * e.speed * stride; // frozen solid: the gait clock stops with it; a turret never walks
    a.blinkT -= dt;
    if (a.blinkT < 0) { a.blink = 0.12; if (a.blinkT < -0.1) { a.blinkT = 2 + ((a.id * 7.3 + time) % 4); a.blink = 1; } }
    if (dying) a.blink = 0.15;

    // ---- root pose (pivot at body centre)
    let yo = 0, pitch = 0, roll = 0, sx = 1, sy = 1, sz = 1;
    const ph = a.phase;
    // idle breathing so a standing crowd never freezes
    const br = Math.sin(time * 1.9 + e.id * 1.3);
    switch (t) {
      case SWARMER_T: {
        yo += Math.abs(Math.sin(ph)) * 0.06 * spdN;
        roll += Math.sin(ph) * 0.07 * spdN;
        sy += br * 0.012 * (1 - spdN);
        // the perch leap: a lunge in the air is the leap (knockback tumbles in any other state)
        const leaping = !dying && st === 2 && (e.flags & F_AIR) !== 0 && !isFlierT(e.type);
        if (leaping) { if (a.leapT < 0) { a.leapT = 0; a.leapDir = hash(e.id * 3.7 + time) < 0.5 ? -1 : 1; } else a.leapT += dt; }
        else { if (a.leapT >= 0) a.landT = 0; a.leapT = -1; if (!dying) a.landT += dt; }
        if (leaping) {
          // spring off (stretch), one full front flip with a corkscrew of roll, tuck, then open
          // up flat for the landing; the flip finishes by ~0.75 s so short hops still complete it
          const lt = a.leapT;
          const f = ease(clamp01((lt - 0.06) / 0.7));
          pitch -= Math.PI * 2 * f;
          roll += Math.sin(f * Math.PI) * 0.55 * a.leapDir;
          const tuck = Math.sin(f * Math.PI);
          const pop = Math.max(0, 1 - lt / 0.12);
          sy *= 1 + 0.28 * pop - 0.12 * tuck; sx *= 1 - 0.1 * pop + 0.08 * tuck; sz *= 1 - 0.1 * pop + 0.08 * tuck;
          break;
        }
        if (a.landT < 0.3 && !dying) {
          // landing: a hard squash, then a little rebound
          const k = a.landT / 0.3;
          const sq = Math.sin(Math.min(1, k * 1.6) * Math.PI) * (1 - k);
          sy *= 1 - 0.32 * sq; sx *= 1 + 0.16 * sq; sz *= 1 + 0.16 * sq; yo -= 0.05 * sq;
        }
        if (st === 1 && e.hasAim && e.aimY - e.y > 2) {
          // the leap crouch: the toaster's lever goes down, the whole body loads like a spring
          const q = ease(p);
          sy -= 0.3 * q; sx += 0.12 * q; sz += 0.12 * q; yo -= 0.08 * q;
          pitch += 0.1 * q + Math.sin(time * 75) * 0.04 * p;
          roll += Math.sin(time * 57 + e.id) * 0.03 * p;
        } else if (st === 1) { sy -= 0.16 * p; sx += 0.07 * p; sz += 0.07 * p; pitch += 0.22 * ease(p) + Math.sin(time * 60) * 0.03 * p; }
        else if (st === 2) { pitch -= 0.42; sz += 0.14; sy -= 0.08; yo += 0.16 * Math.sin(p * Math.PI); }
        else if (st === 3) { const r = (1 - p) * (1 - p); pitch -= 0.38 * r; sy += 0.05 * Math.sin(p * Math.PI * 2) * (1 - p); }
        break;
      }
      case SHOOTER_T:
        yo += Math.sin(ph * 2) * 0.014 * spdN + br * 0.008;
        roll += Math.sin(ph) * 0.025 * spdN;
        if (st === 1) { pitch += 0.08 * p + Math.sin(time * 70) * 0.012 * p; }
        else if (st === 3) { const r = (1 - p) * (1 - p); pitch += 0.24 * r; yo += 0.03 * r; }
        break;
      case BRUTE_T: case METAL_T:
        // heavy: the body drops on each footfall and rolls over the planted leg
        yo += (Math.abs(Math.cos(ph)) - 0.6) * 0.08 * s * spdN + br * 0.01 * s;
        roll += Math.sin(ph) * 0.06 * spdN;
        if (st === 1) { sy -= 0.08 * p; sx += 0.035 * p; sz += 0.035 * p; pitch += 0.12 * ease(p) + Math.sin(time * 45) * 0.012 * p; yo -= 0.06 * s * ease(p); }
        else if (st === 2) { pitch -= 0.34; yo -= 0.05 * s; }
        else if (st === 3) { pitch -= 0.34 * (1 - ease(p)) + 0.06 * Math.sin(p * Math.PI); }
        break;
      case SNIPER_T:
        pitch -= Math.sin(time * 2.3 + e.id) * 0.025;
        if (st === 1) pitch -= 0.05 * p;
        else if (st === 3) pitch += 0.16 * (1 - p) * (1 - p);
        break;
      case BOMBER_T:
        yo += Math.abs(Math.sin(ph)) * 0.05 * spdN + br * 0.006;
        roll += Math.sin(ph) * 0.09 * spdN;
        if (st === 1) { pitch += 0.14 * ease(p); roll += Math.sin(time * 34) * 0.03 * p; }
        else if (st === 3) pitch -= 0.18 * Math.sin(p * Math.PI);
        break;
      case DISRUPTOR_T:
        yo += Math.abs(Math.sin(ph)) * 0.045 * spdN + br * 0.01;
        roll += Math.sin(ph) * 0.06 * spdN;
        if (st === 1) { yo += 0.06 * ease(p); roll += Math.sin(time * 50) * 0.025 * p; }
        else if (st === 3) pitch += 0.12 * (1 - p) * (1 - p);
        break;
      case ENGINEER_T:
        // a tall tripod: sways over the planted stilts
        yo += Math.sin(ph * 1.5) * 0.04 * spdN + br * 0.012;
        roll += Math.sin(ph * 1.5) * 0.05 * spdN + Math.sin(time * 1.1 + e.id) * 0.015;
        pitch += Math.cos(ph * 1.5) * 0.03 * spdN;
        if (st === 1) pitch += 0.06 * p;
        break;
    }
    if (!dying) {
      // idle life, big enough that frames 2 s apart differ: a slow bob, a side-to-side sway
      // (antennas whip with it) and a nod; fades as the robot gets moving
      const idleK = 1 - Math.min(1, spdN) * 0.6;
      yo += (0.5 + 0.5 * Math.sin(time * 2.1 + e.id * 1.3)) * 0.035 * s * idleK;
      roll += Math.sin(time * 1.6 + e.id) * 0.045 * idleK;
      pitch += Math.sin(time * 1.15 + e.id * 2.1) * 0.03 * idleK;
      // weight shift onto each planted foot
      roll += Math.sin(a.phase) * 0.05 * spdN;
      yo -= Math.abs(Math.cos(a.phase)) * 0.03 * spdN * s;
      // lean into acceleration and into turns (weight)
      pitch -= Math.max(-0.16, Math.min(0.16, a.acc * 0.03)) + 0.04 * spdN * (t === SNIPER_T || t === SHOOTER_T ? 1.5 : 1);
      roll -= Math.max(-0.12, Math.min(0.12, a.turn * a.spd * 0.012));
    }
    const slowed = ((e.flags2 ?? 0) & 1) !== 0 && !dying;
    let hoxSlow = 0, hozSlow = 0;
    if (slowed) {
      // caught in a Time grenade (or iced): it stutters, held and released at a few frames a second, and shivers
      const st8 = Math.floor(time * 7 + e.id) / 7;
      pitch += Math.sin(st8 * 50) * 0.06; roll += Math.cos(st8 * 37) * 0.06;
      yo += (Math.sin(time * 90 + e.id) > 0.9 ? 0.04 : 0);
      // playtest 2 (Nick): the wiggle blur: a fast side-to-side shiver and a violet smear (the body jitters a few cm at ~25 Hz)
      hoxSlow = Math.sin(time * 157 + e.id * 3) * 0.07; hozSlow = Math.cos(time * 131 + e.id) * 0.05;
      roll += Math.sin(time * 171 + e.id) * 0.09; pitch += Math.sin(time * 143 + e.id * 2) * 0.05;
    }
    if (stunned && !frozen) {
      pitch += Math.sin(time * 16 + e.id) * 0.13;
      roll += Math.cos(time * 13 + e.id * 2) * 0.13;
      yo += Math.abs(Math.sin(time * 31 + e.id)) * 0.03;
    }
    // hit flinch: knocked back, then a damped wobble back to stance
    // shooting feel: the flinch goes AWAY from the shot (pitch for front/back, roll for the sides),
    // scales with how hard the hit was against its hull, and a heavy hit staggers it: a slower,
    // deeper sway, the knees buckle and the whole robot is shoved along the blow
    let hox = 0, hoz = 0;
    if (e.hitAge < 0.8 && !dying) {
      const h = e.hitAge;
      const pow = Math.min(2, a.hpow > 0 ? a.hpow : 0.35);
      const big = pow > 0.55;
      const cy = Math.cos(e.yaw), sn = Math.sin(e.yaw);
      let lx = cy * a.hdx - sn * a.hdz, lz = sn * a.hdx + cy * a.hdz;
      if (lx * lx + lz * lz < 0.01) { lx = 0; lz = 1; }
      const w = FLINCH[t] * (0.55 + 0.9 * pow) * Math.exp(-h * (big ? 5.5 : 9)) * Math.cos(h * (big ? 15 : 24));
      pitch += w * lz;
      roll -= w * lx * 0.8;
      const shove = (big ? 0.16 : 0.06) * Math.min(1.5, pow) * (1 - Math.exp(-h * 40)) * Math.exp(-h * 5) / Math.sqrt(s);
      hox = a.hdx * shove; hoz = a.hdz * shove;
      if (big) yo -= 0.09 * s * Math.min(1.5, pow) * Math.sin(Math.min(1, h * 3) * Math.PI);
      if (h < 0.12) { const k = (1 - h / 0.12) * (0.7 + 0.4 * Math.min(1, pow)); sy *= 1 - 0.08 * k; sx *= 1 + 0.06 * k; sz *= 1 + 0.06 * k; }
    }
    if (isFlierT(e.type) && (e.flags & F_AIR) && !dying) {
      // a flier hovers: a slow bob, a lean into its travel, a little sway; the helicopter wobbles like a heavy load
      yo += Math.sin(time * 1.6 + e.id) * 0.12;
      pitch += Math.max(-0.25, Math.min(0.25, a.acc * 0.04)) + Math.sin(time * 1.1 + e.id) * 0.05;
      roll += Math.sin(time * 0.9 + e.id * 2) * (e.type === HELI_T ? 0.08 : 0.05) - Math.max(-0.2, Math.min(0.2, a.turn * a.spd * 0.02));
    } else if ((e.flags & F_AIR) && a.leapT < 0) { pitch += time * 7 + e.id; roll += Math.sin(time * 3 + e.id) * 0.4; }
    if (st === 5 && !dying) {
      const k = ease(p);
      yo -= (1 - k) * top * 0.9;
      sy *= 0.55 + 0.45 * k; sx *= 0.85 + 0.15 * k; sz *= 0.85 + 0.15 * k;
      roll += Math.sin(time * 20 + e.id) * 0.08 * (1 - k);
    }

    // R = T(pos) · Ry(yaw) · [topple about the back edge] · T(piv) · Rx(pitch)Rz(roll) · T(-piv) · S
    const piv = def.bodyY;
    const R = this.R, M = this.T;
    R.makeRotationY(e.yaw);
    R.setPosition(e.x + hox + hoxSlow, e.y + yo, e.z + hoz + hozSlow);
    if (dying) {
      // hit-stop: the corpse holds its hit pose (flashing) for DIE_HOLD, then goes. It falls AWAY
      // from the killing blow: the legs give way (gravity-eased drop onto the body's own underside,
      // 0.18 s), then it tips over the edge on the far side and lands by ~0.4 s. A blown robot
      // (explosions, detonations, big shotgun hits) is thrown off its feet first: an arc with a
      // tumble that ends on its back, then a bounce.
      const side = hash(a.id) < 0.5 ? 1 : -1;
      const bot = BOT[t] * s;
      const hold = this.hold;
      const fT = Math.max(0, dT - hold);
      const edge = lay.depth * s;
      const sink = dT > 2.5 ? ease(clamp01((dT - 2.5) / 0.85)) : 0;
      // local fall direction: robot-local +z (its back) rotated to face along the push
      const cy = Math.cos(e.yaw), sy0 = Math.sin(e.yaw);
      const lx = cy * a.ddx - sy0 * a.ddz, lz = sy0 * a.ddx + cy * a.ddz;
      const th = lx * lx + lz * lz > 0.01 ? Math.atan2(lx, lz) : 0;
      let ox = 0, oy = 0, oz = 0, tumble = 0, fall: number, drop: number;
      if (a.dKind === DeathKind.Blown) {
        const g = 22, tl = (2 * a.bvy) / g;
        const ft = Math.min(fT, tl);
        ox = a.bvx * ft + (fT > tl ? a.bvx * 0.12 * (1 - Math.exp(-(fT - tl) * 8)) : 0);
        oz = a.bvz * ft + (fT > tl ? a.bvz * 0.12 * (1 - Math.exp(-(fT - tl) * 8)) : 0);
        oy = a.bvy * ft - 0.5 * g * ft * ft;
        // whole turns in the air so it lands lying on its back (1.4 rad), then a small bounce
        const turns = Math.round(Math.abs(a.bspin) * tl / 6.283);
        tumble = fT < tl ? (1.4 + turns * 6.283) * (fT / tl) : 1.4 + turns * 6.283 + 0.25 * Math.sin(Math.min(1, (fT - tl) * 5) * Math.PI) * Math.exp(-(fT - tl) * 4);
        if (fT > tl) oy += Math.abs(Math.sin(Math.min(1, (fT - tl) * 3.2) * Math.PI)) * 0.35 * Math.min(1, a.bvy / 8) * Math.exp(-(fT - tl) * 2);
        fall = 0; drop = clamp01(fT / 0.12);
      } else {
        drop = clamp01(fT / 0.18);
        fall = outBounce(clamp01((fT - 0.1) / 0.3));
        if (a.dKind === DeathKind.Headshot) fall = outBounce(clamp01((fT - 0.16) / 0.34)); // a beat of standing dead
      }
      R.setPosition(e.x + ox, e.y + Math.max(-0.2, oy), e.z + oz);
      R.multiply(M.makeTranslation(0, -sink * 0.9 * s - bot * drop * drop, 0));
      R.multiply(M.makeRotationY(th));
      R.multiply(M.makeTranslation(0, bot, edge));
      R.multiply(M.makeRotationFromEuler(this.eu.set(fall * 1.4 + tumble, 0, fall * 0.25 * side, 'XZY')));
      R.multiply(M.makeTranslation(0, -bot, -edge));
      R.multiply(M.makeRotationY(-th));
      if (dT < hold) {
        // frozen mid-flinch: knocked a little along the blow, squashed by it
        const k = 1 - dT / hold;
        pitch = 0.12 * Math.min(1.5, a.dpow) * k; roll = 0; sx = 1 + 0.06 * k; sy = 1 - 0.08 * k; sz = 1 + 0.06 * k;
      } else { pitch = 0; roll = 0; sx = 1; sy = 1; sz = 1; }
    }
    if (dying && a.rag) {
      // the ragdoll owns every part in world space; the root is where it died (parts are written relative to it),
      // sinking away with the corpse at the end
      const sink = dT > 2.5 ? ease(clamp01((dT - 2.5) / 0.85)) : 0;
      R.copy(a.rag.root);
      a.rag.root.elements[13] = e.y - sink * 1.2 * s; a.rag.rootInv.copy(a.rag.root).invert();
      pitch = 0; roll = 0; sx = 1; sy = 1; sz = 1;
    }
    R.multiply(M.makeTranslation(0, piv, 0));
    R.multiply(M.makeRotationFromEuler(this.eu.set(pitch, 0, roll, 'XZY')));
    R.multiply(M.makeTranslation(0, -piv, 0));
    R.multiply(M.makeScale(sx, sy, sz));

    const kit = this.kits[t];
    this.cur = kit;
    if (!kit.b.robot(R, f)) return;
    // ---- the shield skin (fx/shieldskin.ts): hugs the parts; flashes from a strike; shatters when it goes (a kill
    // through the shield shatters it too)
    {
      const k = dying ? 0 : clamp01(e.shield);
      if (a.shPrev > 0.01 && k <= 0.01) { a.shBrkAt = time; this.onShieldBreak?.(e.x, e.y, e.z, top); }
      a.shPrev = k;
      const ha = time - a.shHitAt, ba = time - a.shBrkAt;
      kit.b.shieldOf(k, ha < 0.7 ? ha : -1, ba < 0.5 ? ba : -1, f.seed, a.shX, a.shY, a.shZ);
    }
    // contact blob: stays on the ground under the robot (shrinks as it hops, fades as a corpse sinks)
    {
      const fp = BLOB[t];
      let ba = 0.72 - Math.min(0.4, Math.max(0, yo) * 1.5);
      if (e.flags & F_AIR) ba *= 0.35;
      let bx = e.x, bz = e.z, bl = fp[1] * s;
      if (dying) {
        // follows the corpse: under its toppled body centre, stretched along the fall, fading as it sinks
        ba *= 1 - clamp01((dT - 2.4) / 0.9);
        const v = this.v.set(0, def.bodyY * 0.6, 0).applyMatrix4(R);
        bx = v.x; bz = v.z;
        bl += top * 0.6 * clamp01((dT - 0.1) / 0.3);
      }
      this.blobs.push(bx, e.y + 0.05, bz, e.yaw, fp[0] * s, bl, ba);
    }
    this.Ri.copy(R).invert();
    switch (t) {
      case SWARMER_T: this.swarmer(e, a, kit, p, spdN, time); break;
      case SHOOTER_T: this.shooter(e, a, kit, p, spdN, dt, time); break;
      case BRUTE_T: case METAL_T: this.brute(e, a, kit, p, spdN, dt, time, s); break;
      case SNIPER_T: this.sniper(e, a, kit, p, spdN, dt, time); break;
      case BOMBER_T: this.bomber(e, a, kit, p, spdN, dt, time); break;
      case DISRUPTOR_T: this.disruptor(e, a, kit, p, spdN, dt, time); break;
      case ENGINEER_T: this.engineer(e, a, kit, p, spdN, dt, time); break;
    }

    // ---- eyes (a ragdolled corpse's eyes go with its parts: none drawn)
    if (!(dying && a.rag)) for (let i = 0; i < lay.eyes.length; i++) {
      const ep = lay.eyes[i];
      this.eye(e, a, kit.s.eye + i, ep[0] * s, ep[1] * s, ep[2] * s, lay.eyeR * s * (metal ? 0.95 : 0.78), stunned, time, i);
    }
    if (dying) return;

    // ---- shells
    if ((e.flags & F_HYPER) || (ext & 64) || ((t === SHOOTER_T || t === SNIPER_T) && !(f2 & 128))) {
      const hr = Math.max(def.radius * 1.3, 0.7), vr = top * 0.5 + 0.18;
      const hitK = e.hitAge < 0.18 ? 1 - e.hitAge / 0.18 : 0;
      // shells thin out with range so a far crowd isn't a wall of bubbles
      const cp = this.ctx.camera.position;
      const far = 1 - 0.65 * clamp01((Math.hypot(e.x - cp.x, e.z - cp.z) - 9) / 18);
      // (the shield itself is the skin above, not a bubble)
      if ((t === SHOOTER_T || t === SNIPER_T) && !(f2 & 128) && !dying) {
        // the gun router's gold tell (weapon_sever.gd): shoot here to disarm it
        this.local(0.55, ENEMIES[e.type].bodyY + 0.1, -0.3, 0, time * 2, 0, 1);
        this.L.scale(this.sc.set(0.14 / sx, 0.14 / sy, 0.14 / sz));
        this.W.multiplyMatrices(R, this.L);
        this.shells.push(this.W, C_GOLD, 0.55 + 0.25 * Math.sin(time * 6 + e.id));
      }
      if (ext & 64) { // Redshell: a red shell only melee breaks
        this.local(0, top * 0.5, 0, 0, -time * 0.5, 0, 1);
        this.L.scale(this.sc.set(hr * 1.08 / sx, vr * 1.06 / sy, hr * 1.08 / sz));
        this.W.multiplyMatrices(R, this.L);
        this.shells.push(this.W, C_REDSHELL, 0.4 * far + hitK * 0.8);
      }
      if (e.flags & F_HYPER) {
        this.local(0, top * 0.5, 0, 0, time * 0.4, 0, 1);
        this.L.scale(this.sc.set(hr * 1.12 / sx, vr * 1.1 / sy, hr * 1.12 / sz));
        this.W.multiplyMatrices(R, this.L);
        this.shells.push(this.W, C_HYPER, (0.55 + 0.2 * Math.sin(time * 5 + e.id)) * (0.4 + 0.6 * far) + hitK * 0.8);
      }
    }

    // ---- primed diamond (world-upright, never tumbles)
    if (e.flags & F_PRIMED) {
      const pulse = 0.5 + 0.5 * Math.sin(time * 7 + e.id);
      this.col.copy(C_MAG).lerp(C_AMB, pulse).multiplyScalar(1.5);
      const ds = metal ? 0.34 : 0.22;
      this.q.setFromEuler(this.eu.set(0, time * 2.6 + e.id, 0));
      this.W.compose(this.v.set(e.x, e.y + yo + top + 0.38 * s + Math.sin(time * 3 + e.id) * 0.06, e.z), this.q, this.sc.set(ds, ds * (1 + 0.15 * pulse), ds));
      this.diamonds.push(this.W, this.col);
    }
  }

  /** L = local transform; returns L */
  private local(x: number, y: number, z: number, rx: number, ry: number, rz: number, s: number) {
    this.q.setFromEuler(this.eu.set(rx, ry, rz));
    return this.L.compose(this.v.set(x, y, z), this.q, this.sc.set(s, s, s));
  }
  /**
   * Write a part (local to the robot root). While dying, every part but the body pops off:
   * a short hop outward and a tumble, then it lies where it landed.
   */
  private pt(slot: number, x: number, y: number, z: number, rx: number, ry: number, rz: number, s: number, glow: number) {
    if (this.dying) {
      const a = this.dieAnim!;
      if (a.rag) {
        // ragdoll: the rigid body's pose, written robot-local
        const b = a.rag.bodies[slot];
        if (!b) return;
        const bp = b.position, bq = b.quaternion;
        this.W.compose(this.v.set(bp.x, bp.y, bp.z), this.q.set(bq.x, bq.y, bq.z, bq.w), this.sc.set(s, s, s));
        this.T.multiplyMatrices(a.rag.rootInv, this.W);
        this.cur.b.part(slot, this.T, glow * 0.3);
        return;
      }
      if (a.ragWant) a.ragLocals[slot] = [x, y, z, rx, ry, rz, s];
    }
    if (this.dying && slot === 0 && this.shatter > 0) s *= 1 - this.shatter; // the shell bursts into the debris FX
    if (!this.dying && this.hiddenSlots && ((this.hiddenSlots >> slot) & 1)) s = 0; // a part shot off while it lives (playtest 3)
    if (this.dying && slot > 0) {
      const a = this.dieAnim!;
      const h1 = hash(this.dieSeed * 3.1 + slot), h2 = hash(this.dieSeed * 7.7 + slot * 1.3);
      const fT = Math.max(0, this.dT - this.hold);
      const tt = this.cur === this.kits[SWARMER_T] && slot <= 2 ? 1.6 : 1; // the toast really pops
      const k = 1 - Math.exp(-fT * 7);
      // how far parts fly: a plain death sheds them, a blast or a shatter throws them clear
      const far = a.dKind === DeathKind.Blown ? 3.2 : a.dKind === DeathKind.Shatter ? 5 : 1;
      const fly = (0.1 + 0.18 * h2) * far;
      x += (x === 0 ? h1 - 0.5 : Math.sign(x)) * fly * k;
      z += (h2 - 0.5) * fly * k;
      const hop = a.dKind === DeathKind.Shatter ? 0.9 : a.dKind === DeathKind.Blown ? 0.6 : 0.35;
      y += Math.sin(Math.min(1, fT * 2.2) * Math.PI) * hop * tt;
      rx += k * (h1 - 0.5) * 5 * far; rz += k * (h2 - 0.5) * 4 * far;
      if (this.headSlot >= 0 && this.dT >= this.hold && slot >= this.headSlot && (slot === this.headSlot || (this.cur === this.kits[SWARMER_T] && slot <= this.headSlot + 1))) {
        // headshot kill: the top part (toast, fan, lid, lamp) is shot clean off; FeelFx throws a
        // world-space copy of it in a tall spinning arc, so the real one is gone from here on
        s = 0;
      }
      glow *= 1 - k;
    }
    this.local(x, y, z, rx, ry, rz, s);
    this.cur.b.part(slot, this.L, glow);
  }
  /** write a part whose local matrix is already in this.W */
  private ptW(slot: number, glow: number) { this.cur.b.part(slot, this.W, glow); }

  private eye(e: EnemyRS, a: Anim, slot: number, lx: number, ly: number, lz: number, r: number, stunned: boolean, time: number, i: number) {
    const pos = this.v2.set(lx, ly, lz).applyMatrix4(this.R);
    const el = this.R.elements;
    const fwd = this.v3.set(-el[8], -el[9], -el[10]).normalize();
    const d = this.v;
    if (stunned || this.dying) {
      const ang = time * 9 + i * 1.3;
      d.set(Math.cos(ang) * 0.7, Math.sin(ang) * 0.7, 0);
      d.applyMatrix4(this.T.extractRotation(this.R)).add(fwd).normalize();
    } else {
      d.set(e.lookX - pos.x, e.lookY - pos.y, e.lookZ - pos.z);
      const len = d.length();
      if (len < 1e-3) d.copy(fwd); else d.multiplyScalar(1 / len);
      const dot = d.dot(fwd);
      if (dot < 0.45) d.addScaledVector(fwd, 0.45 - dot).normalize();
      // a little independent jiggle so a crowd doesn't look like one rig
      d.x += Math.sin(time * 1.7 + a.id + i) * 0.04;
      d.normalize();
    }
    const W = this.W;
    W.lookAt(this.zero, d, this.up);
    W.scale(this.sc.set(r, r * a.blink, r));
    W.setPosition(pos);
    // to robot-local
    this.T.multiplyMatrices(this.Ri, W);
    this.cur.b.part(slot, this.T, 0);
  }

  // ------------------------------------------------------------------ types
  private swarmer(e: EnemyRS, a: Anim, k: TypeKit, p: number, spdN: number, time: number) {
    const st = e.st, S = k.s;
    let rise = 0.05, glow = 0.6 + 0.15 * Math.sin(time * 6 + e.id);
    const crouch = st === 1 && e.hasAim && e.aimY - e.y > 2;
    if (a.leapT >= 0) {
      // POP: the toast shoots up out of the slots at take-off and drops back in as it lands
      const lt = a.leapT;
      rise = 0.05 + 0.42 * Math.min(1, lt / 0.08) * Math.max(0.45, 1 - lt / 1.2) + Math.sin(time * 40) * 0.01;
      glow = 2.2;
    } else if (crouch) { rise = 0.05 - 0.09 * ease(p) + Math.sin(time * 70) * 0.008 * p; glow = 0.5 + 1.9 * p * p; }
    else if (st === 1) { rise = 0.05 + 0.2 * ease(p) + Math.sin(time * 50) * 0.014 * p; glow = 0.4 + 1.6 * p; }
    else if (st === 2) { rise = 0.26; glow = 2.0; }
    else if (st === 3) { rise = 0.05 + 0.21 * (1 - p); glow = 0.4 + 1.6 * (1 - p); }
    this.pt(S.body, 0, 0, 0, 0, 0, 0, 1, 0);
    for (let i = 0; i < 2; i++) {
      const t = SWARMER.toast[i];
      this.pt(S.toast + i, t[0], t[1] + rise * (i ? 0.9 : 1), t[2], 0, 0, (i ? 0.05 : -0.05) * p, 1, glow);
    }
    const ph = a.phase;
    for (let i = 0; i < 4; i++) {
      const ft = SWARMER.feet[i];
      const off = i === 0 || i === 3 ? 0 : Math.PI;
      let sw = Math.sin(ph + off) * 0.65 * spdN;
      const lift = Math.max(0, Math.cos(ph + off)) * 0.06 * spdN;
      if (st === 2) sw = -0.8;
      if (st === 1) sw = 0.25 * p;
      // in the leap the feet tuck up under the body, then swing down for the landing
      if (a.leapT >= 0) { const f = clamp01((a.leapT - 0.06) / 0.7); sw = 1.1 * Math.sin(f * Math.PI) - 0.3 * (1 - f); }
      this.pt(S.foot + i, ft[0], ft[1] + lift, ft[2], sw, 0, 0, 1, 0);
    }
  }

  private shooter(e: EnemyRS, a: Anim, k: TypeKit, p: number, spdN: number, dt: number, time: number) {
    const st = e.st, S = k.s;
    const target = this.dying ? 0 : st === 1 ? 5 + 75 * p * p : 5;
    a.fanRate += (target - a.fanRate) * (st === 1 ? 1 : Math.min(1, dt * 1.8));
    a.fan += a.fanRate * dt;
    const glow = clamp01((a.fanRate - 20) / 55) * 0.7;
    this.pt(S.body, 0, 0, 0, 0, 0, 0, 1, 0);
    const fp = SHOOTER.fan;
    this.pt(S.fan, fp[0], fp[1], fp[2], 0, 0, a.fan, 1, glow);
    if (a.fanRate > 28 && this.hiQ) {
      this.pt(S.fan + 1, fp[0], fp[1], fp[2] - 0.004, 0, 0, a.fan + 0.42, 1, glow);
      this.pt(S.fan + 2, fp[0], fp[1], fp[2] - 0.008, 0, 0, a.fan + 0.84, 1, glow);
    }
    const ph = a.phase;
    for (let i = 0; i < 2; i++) {
      const t = SHOOTER.tracks[i];
      this.pt(S.track + i, t[0], t[1] + Math.max(0, Math.sin(ph * 3 + i * Math.PI)) * 0.018 * spdN, t[2], Math.sin(ph * 3 + i) * 0.025 * spdN, 0, 0, 1, 0);
    }
    for (let i = 0; i < 2; i++) {
      const t = SHOOTER.arms[i], side = i ? 1 : -1;
      let rx = Math.sin(ph + i * Math.PI) * 0.35 * spdN + Math.sin(time * 1.9 + e.id + i) * 0.05, rz = 0;
      if (st === 1) { rx = 0.6 * p + Math.sin(time * 30 + i) * 0.15 * p; rz = side * 0.4 * p; }
      else if (st === 3) { const r = 1 - p; rx = -0.4 * r; rz = side * 0.5 * r; }
      this.pt(S.arm + i, t[0], t[1], t[2], rx, 0, rz, 1, 0);
    }
  }

  private brute(e: EnemyRS, a: Anim, k: TypeKit, p: number, spdN: number, dt: number, time: number, s: number) {
    const st = e.st, S = k.s;
    const REST = Math.PI - 0.35, DOWN = 0.12;
    let lt = REST, vent = 0;
    if (st === 1) { lt = REST + (DOWN - REST) * ease(p); vent = Math.pow(p, 1.5) * 1.5 * (0.8 + 0.2 * Math.sin(time * 40)); }
    else if (st === 2) { lt = DOWN; vent = 1.1 * (1 - p * 0.5); }
    else if (st === 3) { lt = DOWN + (REST - DOWN) * ease(p); vent = 0.6 * (1 - p); }
    a.lever += (lt - a.lever) * Math.min(1, dt * (st === 1 ? 30 : 10));
    this.pt(S.body, 0, 0, 0, 0, 0, 0, 1, vent);
    const lv = BRUTE.lever;
    this.pt(S.lever, lv[0] * s, lv[1] * s, lv[2] * s, 0, 0, a.lever, 1, 0);
    const ph = a.phase;
    for (let i = 0; i < 2; i++) {
      const t = BRUTE.legs[i];
      const sn = Math.sin(ph + i * Math.PI);
      let rx = sn * 0.5 * spdN;
      const lift = Math.max(0, -Math.cos(ph + i * Math.PI)) * 0.1 * s * spdN;
      if (st === 2) rx = Math.sin(time * 14 + i * Math.PI) * 0.55;
      if (st === 1) rx = (i ? -0.2 : 0.25) * ease(p); // braces its stance
      this.pt(S.leg + i, t[0] * s, t[1] * s + lift, t[2] * s, rx, 0, 0, 1, 0);
    }
    for (let i = 0; i < 2; i++) {
      const t = BRUTE.arms[i], side = i ? 1 : -1;
      let rx = -Math.sin(ph + i * Math.PI) * 0.32 * spdN + Math.sin(time * 1.9 + e.id) * 0.03, rz = side * 0.08;
      if (st === 1) { rx = 1.7 * ease(p); rz = side * 0.3 * p; }
      else if (st === 2) { rx = -0.8 * outBack(clamp01(p * 3)); rz = side * 0.3; }
      else if (st === 3) { rx = -0.8 * (1 - ease(p)); }
      this.pt(S.arm + i, t[0] * s, t[1] * s, t[2] * s, rx, 0, rz, 1, 0);
    }
  }

  private sniper(e: EnemyRS, a: Anim, k: TypeKit, p: number, spdN: number, dt: number, time: number) {
    const st = e.st, S = k.s;
    if (!this.dying) a.wheel -= (e.speed * dt) / SNIPER.wheelR;
    this.pt(S.body, 0, 0, 0, 0, 0, 0, 1, 0);
    for (let i = 0; i < 2; i++) {
      const w = SNIPER.wheels[i];
      this.pt(S.wheel + i, w[0], w[1], w[2], a.wheel, 0, 0, 1, 0);
    }
    // rifle tracks the aim pitch during the windup
    let tgt = 0;
    if (st === 1 && e.hasAim) {
      const dy = e.aimY - (e.y + SNIPER.rifle[1]);
      const h = Math.hypot(e.aimX - e.x, e.aimZ - e.z);
      tgt = Math.max(-0.6, Math.min(0.6, Math.atan2(dy, h)));
    }
    a.rifle += (tgt - a.rifle) * Math.min(1, dt * 8);
    let kick = 0, kickR = 0, glow = 0.15;
    if (st === 1) glow = (0.35 + 1.8 * p) * (0.75 + 0.25 * Math.sin(time * 28));
    else if (st === 3) { const r = (1 - p) * (1 - p); kick = 0.2 * r; kickR = 0.3 * r; glow = 0.15 + 1.4 * r; }
    const rp = SNIPER.rifle;
    this.pt(S.rifle, rp[0], rp[1], rp[2] + kick, a.rifle + kickR, -0.16, 0, 1, glow);
    if (st === 1 && e.hasAim && !this.dying) {
      const sc = SNIPER.scope;
      const from = this.v2.set(sc[0], sc[1], sc[2] - 0.01).applyMatrix4(this.L).applyMatrix4(this.R);
      const to = this.v3.set(e.aimX, e.aimY, e.aimZ);
      const lock = p > 0.82;
      const kk = lock ? 1.6 * (0.55 + 0.45 * (Math.sin(time * 60) > 0 ? 1 : 0)) : 0.45 + 0.65 * p;
      this.lasers.push(from, to, kk);
    }
  }

  private bomber(e: EnemyRS, a: Anim, k: TypeKit, p: number, spdN: number, dt: number, time: number) {
    const st = e.st, S = k.s;
    const rate = this.dying ? 0 : st === 1 ? 3 + 28 * p : st === 3 ? 10 * (1 - p) + 1.5 : 1.5;
    a.drum += rate * dt;
    let doorGlow = 0.0, lid = 0.45 + Math.sin(time * 2 + e.id) * 0.03, armRx = Math.sin(a.phase) * 0.25 * spdN, fuse = 0.4;
    if (st === 1) {
      doorGlow = 0.08 + 0.5 * p; // a dim ember behind dark glass, never a lamp
      lid += Math.abs(Math.sin(time * 30)) * 0.18 * p;
      armRx = -2.0 * ease(p);
      fuse = 0.6 + 1.6 * p * (0.6 + 0.4 * Math.sin(time * 40));
    } else if (st === 3) {
      doorGlow = 0.3 * (1 - p);
      armRx = 1.1 * (1 - ease(p));
    }
    this.pt(S.body, 0, 0, 0, 0, 0, 0, 1, 0);
    const lp = BOMBER.lid, dp = BOMBER.door, ap = BOMBER.arm;
    this.pt(S.lid, lp[0], lp[1], lp[2], lid, 0, 0, 1, 0);
    this.pt(S.door, dp[0], dp[1], dp[2], 0, 0, a.drum, 1, doorGlow);
    // arm, then the bomb in its hand
    this.pt(S.arm, ap[0], ap[1], ap[2], armRx, 0, 0.12, 1, 0);
    let bs = 1;
    if (st === 3) bs = p < 0.45 ? 0 : ease((p - 0.45) / 0.55);
    if (bs > 0.01) {
      this.T.copy(this.L);
      const hp = BOMBER.hand;
      this.local(hp[0], hp[1], hp[2], 0, time * 0.5, 0, bs);
      this.W.multiplyMatrices(this.T, this.L);
      this.ptW(S.bomb, fuse);
    }
    const ph = a.phase;
    for (let i = 0; i < 2; i++) {
      const t = BOMBER.legs[i];
      const rx = Math.sin(ph + i * Math.PI) * 0.5 * spdN;
      this.pt(S.leg + i, t[0], t[1] + Math.max(0, -Math.cos(ph + i * Math.PI)) * 0.06 * spdN, t[2], rx, 0, 0, 1, 0);
    }
  }

  private disruptor(e: EnemyRS, a: Anim, k: TypeKit, p: number, spdN: number, dt: number, time: number) {
    const st = e.st, S = k.s;
    this.pt(S.body, 0, 0, 0, 0, 0, 0, 1, 0);
    // the screen face carries the telegraph: smile → angry → static at the release
    const fp = DISRUPTOR.face;
    const mood = this.dying ? 2 : st === 1 ? (p < 0.55 ? 1 : 2) : st === 2 ? 2 : st === 3 ? (p < 0.4 ? 2 : 0) : 0;
    const fglow = mood === 2 ? 0.6 + 0.4 * Math.sin(time * 40) : mood === 1 ? 0.25 : 0.12;
    this.pt(mood === 0 ? S.face : mood === 1 ? S.angry : S.stat, fp[0], fp[1], fp[2], 0, 0, mood === 2 ? Math.sin(time * 70) * 0.01 : 0, 1, fglow);
    // casters swivel with heading changes; the chair spins on the windup
    a.drum += (st === 1 ? 6 * p : 0) * dt;
    const cp = DISRUPTOR.caster;
    this.pt(S.caster, cp[0], cp[1], cp[2], 0, a.drum + Math.sin(time * 0.7 + e.id) * 0.2, 0, 1, 0);
    const ph = a.phase;
    for (let i = 0; i < 2; i++) {
      const t = DISRUPTOR.legs[i];
      const rx = Math.sin(ph + i * Math.PI) * 0.45 * spdN;
      this.pt(S.leg + i, t[0], t[1] + Math.max(0, -Math.cos(ph + i * Math.PI)) * 0.05 * spdN, t[2], rx, 0, 0, 1, 0);
    }
    const ap = DISRUPTOR.arm;
    let arx = Math.sin(ph) * 0.2 * spdN + Math.sin(time * 1.6 + e.id) * 0.06, arz = 0.1;
    if (st === 1) { arx = -1.2 * ease(p); arz = 0.3 * p; }
    this.pt(S.arm, ap[0], ap[1], ap[2], arx, 0, arz, 1, 0);
  }

  private engineer(e: EnemyRS, a: Anim, k: TypeKit, p: number, spdN: number, dt: number, time: number) {
    const st = e.st, S = k.s;
    this.pt(S.body, 0, 0, 0, 0, 0, 0, 1, 0);
    // copy light: idles parked, sweeps the glass on the windup (build/repair telegraph)
    const lp = ENGINEER.light;
    let lx = -0.25, lg = 0.15;
    if (st === 1) { lx = -0.25 + 0.5 * (0.5 - 0.5 * Math.cos(p * Math.PI * 4)); lg = 1.2 + 0.6 * p; }
    else if (st === 2 || st === 3) { lx = -0.25 + 0.5 * (1 - p); lg = 0.8 * (1 - p) + 0.15; }
    this.pt(S.light, lp[0] + lx, lp[1], lp[2], 0, 0, 0, 1, lg);
    // tripod gait: one stilt lifts at a time
    const ph = a.phase;
    for (let i = 0; i < 3; i++) {
      const t = ENGINEER.stilts[i];
      const c = ph * 1.5 + (i * Math.PI * 2) / 3;
      const lift = Math.max(0, Math.sin(c)) * 0.12 * spdN;
      const sw = Math.cos(c) * 0.22 * spdN;
      this.pt(S.stilt + i, t[0], t[1] + lift, t[2], sw, 0, 0, 1, 0);
    }
    const ap = ENGINEER.arm;
    let arx = Math.sin(time * 1.3 + e.id) * 0.1 + Math.sin(ph) * 0.15 * spdN, arz = 0.15;
    if (st === 1) { arx = -0.9 * ease(p) + Math.sin(time * 22) * 0.25 * p; arz = 0.35 * p; }
    this.pt(S.arm, ap[0], ap[1], ap[2], arx, 0, arz, 1, 0);
  }
}
